export * from './computer';
export * from './runner';
export * from './scheduledTasks';

export type Dictionary<T> = { [key: string]: T };

export type PaginationOptions = {
  page?: number;
  size?: number;
};

export type ServerPaginationOptions = {
  page?: number;
  page_size?: number;
};

export type Page<T> = {
  items: T[];
  limit: number;
  offset: number;
};

export enum Chain {
  Solana = 'solana',
  Opbnb = 'opbnb',
  Bsc = 'bsc',
}

export enum SortType {
  Asc = 'asc',
  Desc = 'desc',
}

export enum SortBy {
  Rank = 'rank',
  Volume = 'volume24hUSD',
  Liquidity = 'liquidity',
}

export type CoinTrendingOptions = {
  chain?: Chain;
  limit?: number;
  offset?: number;
  sort_type?: SortType;
  sort_by?: SortBy;
};

export type TCoinToken = {
  address: string;
  decimals: number;
  name: string;
  symbol: string;
  rank: number;
  volume_24h_usd: number;
  volume_24h_change_percent: number;
  price: number;
  price_24h_change_percent?: number;
  logo_uri: string;
  liquidity: number;
  market_cap: number;
};

export type TCoinTrending = {
  success: boolean;
  data: {
    update_unix_time: number;
    update_time: string;
    tokens: TCoinToken[];
  };
};

export type TNews = {
  id: number;
  hash_id: string;
  title: string;
  content: string;
  source_url?: string;
  published_at: string;
  fetched_at: string;
  processed_at?: string;
  status: string;
  is_important: boolean;
  analysis?: string;
};

export type TUser = {
  id: string;
  walletAddress: string;
  createdAt: string;
};

export type TDarpServer = {
  id: string;
  name: string;
  title: string;
  description: string;
  updated_at: string;
  tools: TDarpServerTool[];
  enabled: boolean;
  logo: string;
  host_type: 'external' | 'internal';
};

export type TDarpServerTool = {
  name: string;
  description: string;
  input_schema: {
    title: string;
    type: string;
    required: string[];
    properties: Dictionary<any>;
  };
};

export type TChat = {
  id: string;
  title: string;
  userId: string;
  createdAt: string;
};

export enum MessageSource {
  User = 'user',
  Llm = 'llm',
  Tool = 'tool',
}

export enum Role {
  User = 'user',
  Assistant = 'assistant',
  Tool = 'tool',
}

export type TMessage = {
  id: string;
  userId: string;
  chatId: string;
  model: string;
  source: MessageSource;
  content: TMessageContent;
  createdAt: string;
};

export type TMessageContent = {
  role: Role;
  content?: string;
  toolCalls?: Array<{
    function: {
      name: string;
      arguments?: string;
    };
    id?: string;
    type?: string;
    result?: any;
  }>;
  toolCallId?: string;
};

export enum WSEventType {
  MessageChunk = 'chat.message.chunk',
  MessageCreated = 'chat.message.created',
  MessageCompleted = 'chat.message.completed',
  ToolCall = 'chat.tool_call',
  ToolCallResult = 'chat.tool_call_result',
}

export interface BaseWSEvent<T extends WSEventType> {
  type: T;
}

export type WSEvent =
  | WSMessageCreatedEvent
  | WSMessageChunkEvent
  | WSMessageCompletedEvent
  | WSToolCallEvent
  | WSToolCallResultEvent;

export interface WSMessageCreatedEvent extends BaseWSEvent<WSEventType.MessageCreated> {
  data: TMessage;
}

export interface WSMessageChunkEvent extends BaseWSEvent<WSEventType.MessageChunk> {
  data: {
    chat_id: string;
    message_id: string;
    content: string;
  };
}

export interface WSMessageCompletedEvent extends BaseWSEvent<WSEventType.MessageCompleted> {
  data: {
    chat_id: string;
    message_id: string;
  };
}

export interface WSToolCallEvent extends BaseWSEvent<WSEventType.ToolCall> {
  data: {
    chat_id: string;
    server_name: string;
    tool_name: string;
    arguments: any;
  };
}

export interface WSToolCallResultEvent extends BaseWSEvent<WSEventType.ToolCallResult> {
  data: {
    chat_id: string;
    server_name: string;
    tool_name: string;
    result: any[];
    success: boolean;
  };
}

// export type CreateMessageResponse = {
//   id: string,
//   userId: string,
//   content: TMessageContent,
//   chatId: string,
//   source: MessageSource,
//   model: string | null,
//   createdAt: string
// };

export type CreateMessageResponse = TMessage;

export type ToolCall = {
  name: string;
  loading: boolean;
  arguments?: any;
  result?: any;
};

export type TDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  assetAddress?: string;
  isMobile?: boolean;
  chainType: Chain;
};

export type TCoinSnippetConfigResponse = {
  supports_search: boolean;
  supports_group_request: boolean;
  supports_marks: boolean;
  supports_timescale_marks: boolean;
  supports_time: boolean;
  exchanges: TExchange[];
  symbols_types: TSymbolType[];
  supported_resolutions: string[];
};

export type TExchange = {
  value: string;
  name: string;
  desc: string;
};

export type TSymbolType = {
  name: string;
  value: string;
};

export type TCoinSnippetTimeResponse = {
  timestamp: number;
};

export type TCoinSnippetSearchRequest = {
  query: string;
  type?: string;
  exchange?: string;
};

export type TCoinSnippetSearchResponse = {
  symbol: string;
  full_name: string;
  description: string;
  exchange: string;
  type: string;
}[];

export type TCoinSnippetHistoryRequest = {
  chain: Chain;
  asset_address: string;
  resolution: string;
  from: string;
  to: string;
};

export type TCoinSnippetHistoryItem = {
  time: number;
  open: number;
  high: number;
  low: number;
  close: number;
  volume: number;
};

export type TCoinSnippetHistoryResponse = {
  items: TCoinSnippetHistoryItem[];
};

export type TCoinSnippetAssetsResponse = {
  created_at: string;
  name: string;
  ticker: string;
  description: string | null;
  marketcap: number;
  liquidity_pool: number;
  volume_24h: number;
  holders: number;
  logo: string;
  price: number;
};

export type TCoinSnippetAssetsValidateResponse = {
  is_address_valid: true;
};

export type TCoinSnippetWalletsRequest = {
  symbol: string;
  wallet: string;
};

export type TCoinSnippetWalletsPosition = {
  usd: string;
  coin: string;
};

export type TCoinSnippetWalletsResponse = {
  wallet_address: string;
  symbol: string;
  position: TCoinSnippetWalletsPosition;
};

export type TTradingDexQuoteRequest = {
  input_mint: string;
  output_mint: string;
  amount: string;
  slippage: string;
};

export type TTradingDexQuoteResponse = {
  inputMint: string;
  inAmount: string;
  outputMint: string;
  outAmount: string;
  otherAmountThreshold: string;
  swapMode: string;
  slippageBps: number;
  platformFee: null;
  priceImpactPct: string;
  routePlan: [
    {
      swapInfo: {
        ammKey: string;
        label: string;
        inputMint: string;
        outputMint: string;
        inAmount: string;
        outAmount: string;
        feeAmount: string;
        feeMint: string;
      };
      percent: number;
    },
  ];
  contextSlot: number;
  timeTaken: number;
};

export type TTradingDexSwapRequest = {
  quote: object;
  userPublicKey: string;
  dynamicComputeUnitLimit: true;
  dynamicSlippage: true;
};

export type SwapResponse = {
  swapTransaction: string;
  lastValidBlockHeight: number;
  prioritizationFeeLamports: number;
  computeUnitLimit: number;
  simulationSlot: number;
  simulationError: null | string;
};

export interface TradingOneInchQuoteRequest {
  src: string;
  dst: string;
  amount: string;
  wallet: string;
  slippage: string;
}

export interface TradingOneInchQuoteResponse {
  dstAmount: string;
  fromTokenAmount: string;
  estimatedGas: string;
  protocols: Array<any>;
  tx: {
    from: string;
    to: string;
    data: string;
    value: string;
    gasPrice: string;
    gas: string;
  };
  fromToken: {
    address: string;
    name: string;
    symbol: string;
    decimals: number;
    logoURI?: string;
  };
  toToken: {
    address: string;
    name: string;
    symbol: string;
    decimals: number;
    logoURI?: string;
  };
}

export interface TradingOneInchSwapRequest {
  src: string;
  dst: string;
  amount: string;
  wallet: string;
  slippage: string;
}

export interface TradingOneInchSwapResponse {
  tx: {
    from: string;
    to: string;
    data: string;
    value: string;
    gasPrice: string;
    gas: string;
  };
  toTokenAmount: string;
  fromTokenAmount: string;
  protocols: Array<any>;
}

export type PancakeChain = 'bsc' | 'opbnb';

export interface PancakeQuoteRequest {
  src: string;
  dst: string;
  amount: string;
  wallet: string;
  slippage: string;
  chain: PancakeChain;
}

export interface PancakeSwapRequest {
  src: string;
  dst: string;
  amount: string;
  wallet: string;
  slippage: string;
  chain: PancakeChain;
}

export interface PancakeQuoteResponse {
  dstAmount?: string;
  fromTokenAmount?: string;
  estimatedGas?: string;
  protocols?: Array<any>;
  tx?: {
    from: string;
    to: string;
    data: string;
    value: string;
    gasPrice?: string;
    gas?: string;
  };
  fromToken?: {
    address: string;
    name: string;
    symbol: string;
    decimals: number;
    logoURI?: string;
  };
  toToken?: {
    address: string;
    name: string;
    symbol: string;
    decimals: number;
    logoURI?: string;
  };
}

export interface PancakeSwapResponse {
  tx: {
    from: string;
    to: string;
    data: string;
    value: string;
    gasPrice?: string;
    gas?: string;
  };
  toTokenAmount?: string;
  fromTokenAmount?: string;
  protocols?: Array<any>;
}

export interface PancakeGasPriceResponse {
  standard: number;
  fast: number;
  instant: number;
}

export interface TransactionData {
  from: string;
  to: string;
  data: string;
  value: string;
  gasLimit: number;
  revertReason?: string;
}

export interface MetaItem {
  type: string;
  value: string | TransactionData;
}

export interface SwapErrorResponse {
  error: string;
  description: string;
  statusCode: number;
  meta: MetaItem[];
  requestId: string;
}

export enum Chain {
  SOLANA = 'solana',
  OPBNB = 'opbnb',
  BSC = 'bsc',
}

export type ToastPosition =
  | 'top-left'
  | 'top-center'
  | 'top-right'
  | 'bottom-left'
  | 'bottom-center'
  | 'bottom-right';

export type AddExternalServerParams = {
  title: string;
  name: string;
  description: string;
  url: string;
  logo: string;
};

export type SearchServersParams = { query: string };

export type AddInternalServerParams = {
  name: string;
  description: string;
  repo_url: string;
  command: string;
  logo: string;
};

export type TSubscription = {
  id: string;
  userId: string;
  status: string;
  plan: string;
  txHash: string;
  walletAddress: string;
  expiresAt: string;
  createdAt: string;
};

export type TSubscriptionPlan = {
  id: string;
  name: string;
  priceWei: string;
  durationDays: number;
  usdtAddress: string;
  treasuryAddress: string;
  chainId: number;
};
